import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import Swal from 'sweetalert2';
import ReactSelect from 'react-select';
import { useUser } from '@clerk/clerk-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../components/ui/card';
import {
  Search,
  Save,
  ArrowLeft,
  Send,
  MessageSquare,
  ImageIcon,
  Upload,
  Plus,
  ArrowUp,
  X,
  Check
} from 'lucide-react';
import {
  selectedClientSelector,
  setSelectedClient,
  clearSelectedClient
} from '../../store/slices/selectedClient';
import { clientsSelector, getClients } from '../../store/slices/clients';
import { authSelector } from '../../store/slices/auth';
import {
  APP_GET_CLIENT,
  API_KEY,
  API_CLIENTS_NAME
} from '../../utils/constants';

const API_SEND_REVIEW_SMS =
  'https://chr6memjga.execute-api.us-east-1.amazonaws.com/prod/sendmessage';

const RequestReviews = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { userInfo } = useSelector(authSelector);
  const { user } = useUser();

  const userEmail =
    user?.primaryEmailAddress?.emailAddress || userInfo?.email || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const getCustomerList = () => {
    if (!customerList?.length) dispatch(getClients());
    return (customerList || []).map((c) => ({
      ...c,
      label: c.clientName,
      value: c.clientName
    }));
  };

  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;
    dispatch(clearSelectedClient());
    try {
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );
      dispatch(setSelectedClient(response?.data || selectedOption));
    } catch {
      dispatch(setSelectedClient(selectedOption));
    }
  };

  const [customMessage, setCustomMessage] = useState(
    '#name, we appreciate you choosing #company. Please help us spread the word about our services by using the review links below.'
  );
  const [mmsImage, setMmsImage] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const normalizePhone = (value) => value.replace(/\D/g, '');

  const buildMessageForCustomer = (name) => {
    // Simply replace placeholders in the current message
    // (which already includes the review links)
    let message = customMessage
      .replace('#name', name)
      .replace('#company', selectedClient?.clientName || 'Your Company');

    return message;
  };

  const handleSendMessage = async () => {
    const confirmedCustomer = customerName.trim();
    const normalizedPhone = normalizePhone(phoneNumber);
    const uid = selectedClient?.appUid || '';
    const userid = selectedClient?.id || '';

    if (confirmedCustomer.length < 3) {
      Swal.fire('Error', 'Please enter customer name', 'error');
      return;
    }

    if (normalizedPhone.length < 10) {
      Swal.fire('Error', 'Please enter a valid phone number', 'error');
      return;
    }

    if (!uid || !userid) {
      Swal.fire(
        'Error',
        'Missing client identifiers (uid/userid). Please select a valid client.',
        'error'
      );
      return;
    }

    const payload = {
      uid,
      userid,
      customer: confirmedCustomer,
      to: `+1${normalizedPhone}`,
      message: buildMessageForCustomer(confirmedCustomer)
    };

    try {
      setIsSending(true);
      await axios.post(API_SEND_REVIEW_SMS, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setCustomerName('');
      setPhoneNumber('');
      setIsSent(true);
      setTimeout(() => setIsSent(false), 3000);
    } catch (error) {
      // The lambda returns a plain-text 200 response without CORS headers,
      // which causes the browser to block reading the response after the SMS
      // is already processed. In this case axios throws a Network Error with
      // no error.response — treat it as a successful send.
      if (!error.response) {
        setCustomerName('');
        setPhoneNumber('');
        setIsSent(true);
        setTimeout(() => setIsSent(false), 3000);
      } else {
        Swal.fire(
          'Error',
          'Unable to send message, please try again.',
          'error'
        );
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.match(/^image\/(jpeg|png)$/)) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        setMmsImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setMmsImage(null);
  };

  // Load client review links
  useEffect(() => {
    const loadClientData = async () => {
      if (selectedClient?.appActive && selectedClient?.appUid) {
        try {
          const res = await axios.get(
            `${APP_GET_CLIENT}?uid=${selectedClient.appUid}`,
            {
              headers: {
                'X-API-KEY': `${API_KEY}`
              }
            }
          );
          if (res.status === 200) {
            const body = res?.data?.body?.[0] || {};
            const {
              gmbShortLink = '',
              yelpShortLink = '',
              message = ''
            } = body;

            // Build full message with review links
            let fullMessage =
              message ||
              '#name, we appreciate you choosing #company. Please help us spread the word about our services by using the review links below.';

            // Add review links if available
            if (gmbShortLink || yelpShortLink) {
              const links = [];
              if (gmbShortLink) links.push(`Google: ${gmbShortLink}`);
              if (yelpShortLink) links.push(`Yelp: ${yelpShortLink}`);
              if (links.length > 0) {
                fullMessage += ' ' + links.join(' ');
              }
            }

            setCustomMessage(fullMessage);
          }
        } catch (error) {
          console.error('Error loading client data:', error);
        }
      }
    };

    loadClientData();
  }, [selectedClient]);

  const previewMessage = buildMessageForCustomer('John');

  return (
    <div className="min-h-screen bg-muted/30 overflow-x-hidden">
      <div className="flex-1 min-w-0">
        <main className="p-3 sm:p-6 lg:p-10 space-y-6 sm:space-y-8">
          {/* Page Title */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
            <div className="flex items-center gap-3 sm:gap-4">
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex-shrink-0"
                onClick={() => history.push('/reviews')}>
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-foreground">
                  Request Reviews,{' '}
                  {selectedClient ? selectedClient.clientName : ''}
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Send review requests to your customers via SMS
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap w-full sm:w-auto">
              {isSuperAdmin && (
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                  <ReactSelect
                    placeholder="Search"
                    options={getCustomerList()}
                    onChange={handleChangeClient}
                    controlShouldRenderValue={false}
                    hideSelectedOptions={true}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    openMenuOnClick={false}
                    openMenuOnFocus={false}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null
                    }}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        height: '56px',
                        borderRadius: '0.75rem',
                        borderWidth: '1px',
                        borderColor: state.isFocused ? '#3767A9' : '#e5e7eb',
                        boxShadow: state.isFocused
                          ? '0 0 0 1px #3767A9'
                          : 'none',
                        backgroundColor: '#ffffff',
                        paddingLeft: '2.5rem',
                        fontSize: '1rem',
                        '&:hover': { borderColor: '#3767A9' }
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#9ca3af',
                        fontSize: '1rem'
                      }),
                      input: (provided) => ({
                        ...provided,
                        fontSize: '1rem',
                        color: '#1e293b'
                      }),
                      menu: (provided) => ({
                        ...provided,
                        borderRadius: '0.75rem',
                        overflow: 'hidden',
                        zIndex: 9999,
                        boxShadow:
                          '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                      }),
                      menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                        color: state.isFocused ? '#1e293b' : '#64748b',
                        cursor: 'pointer',
                        padding: '12px 16px',
                        fontSize: '1rem'
                      })
                    }}
                  />
                </div>
              )}
              <Button
                size="lg"
                style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 w-full sm:w-auto text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3"
                onClick={() => setSuccessModalOpen(true)}>
                <Save className="h-5 w-5" />
                Save Changes
              </Button>
            </div>
          </div>

          {/* Send Review Request */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Send className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    Send Review Request
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    Send a review request to a customer
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 lg:px-8 pb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    Customer Name
                  </Label>
                  <Input
                    placeholder="Enter customer name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="h-12 text-base rounded-xl border-2 focus:border-primary px-4"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    Phone Number
                  </Label>
                  <Input
                    placeholder="Enter phone number"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="h-12 text-base rounded-xl border-2 focus:border-primary px-4"
                  />
                </div>
              </div>
              <div className="mt-6">
                <Button
                  size="lg"
                  onClick={handleSendMessage}
                  disabled={isSending || isSent}
                  className={`h-12 px-8 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-2 ${
                    isSent
                      ? 'bg-green-600 hover:bg-green-600 text-white'
                      : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                  }`}>
                  {isSent ? (
                    <>
                      <Check className="h-5 w-5" />
                      Sent
                    </>
                  ) : isSending ? (
                    <>
                      <Send className="h-5 w-5" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Message Configuration */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    Review Request Message
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    Customize review request messages
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 lg:px-8 pb-8">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* iPhone Preview */}
                <div className="flex justify-center lg:justify-start shrink-0">
                  <div className="relative w-[220px] sm:w-[260px]">
                    <div className="bg-foreground rounded-[3rem] p-[10px] shadow-2xl">
                      <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[100px] h-[24px] bg-foreground rounded-b-2xl z-10 flex items-center justify-center">
                        <div className="w-[50px] h-[4px] bg-muted-foreground/30 rounded-full" />
                      </div>
                      <div className="bg-background rounded-[2.4rem] overflow-hidden">
                        <div className="h-10" />
                        <div className="bg-primary/5 border-b border-border px-4 py-2.5">
                          <p className="text-xs font-semibold text-foreground text-center">
                            Messages
                          </p>
                        </div>
                        <div className="px-3 py-4 min-h-[300px] sm:min-h-[380px] bg-background">
                          <div className="flex justify-start mb-2">
                            <div className="bg-primary/10 rounded-2xl rounded-bl-md px-3 py-2.5 max-w-[90%]">
                              <p className="text-[11px] text-foreground leading-relaxed">
                                {previewMessage}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-border px-2.5 py-2 flex items-center gap-1.5 bg-muted/30">
                          <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                            <Plus className="h-3 w-3 text-muted-foreground" />
                          </div>
                          <div className="flex-1 bg-background border border-border rounded-full px-3 py-1.5">
                            <span className="text-[10px] text-muted-foreground">
                              iMessage
                            </span>
                          </div>
                          <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                            <ArrowUp className="h-3 w-3 text-primary-foreground" />
                          </div>
                        </div>
                        <div className="flex justify-center py-1.5">
                          <div className="w-[80px] h-[3px] bg-muted-foreground/30 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inputs */}
                <div className="flex-1 space-y-6">
                  {/* Custom Message */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">
                      Custom Message
                    </Label>
                    <Textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      className="min-h-[220px] text-base rounded-xl border-2 focus:border-primary p-4 resize-none"
                    />
                  </div>

                  {/* MMS Section */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      Attach an Image (MMS)
                    </Label>
                    <div className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center min-h-[120px] bg-muted/20">
                      {mmsImage ? (
                        <div className="relative">
                          <img
                            src={mmsImage}
                            alt="MMS preview"
                            className="max-h-[100px] rounded-lg object-contain"
                          />
                          <button
                            onClick={removeImage}
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:bg-destructive/90">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No images
                        </p>
                      )}
                    </div>
                    <label htmlFor="mms-upload-request-reviews">
                      <Button
                        variant="outline"
                        className="w-full gap-2 h-11 rounded-xl text-base cursor-pointer"
                        asChild>
                        <span>
                          <Upload className="h-4 w-4" />
                          Add Images
                        </span>
                      </Button>
                    </label>
                    <input
                      id="mms-upload-request-reviews"
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                    <p className="text-sm text-destructive font-medium">
                      Only .jpg/.png images will be accepted
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bottom Actions */}
          <div className="pt-4 pb-8 flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
            <Button
              variant="outline"
              size="lg"
              className="h-12 sm:h-14 px-8 text-base sm:text-lg font-semibold rounded-xl w-full sm:w-auto"
              onClick={() => history.push('/reviews')}>
              Close
            </Button>
            <Button
              size="lg"
              style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 w-full sm:w-auto text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3"
              onClick={() => setSuccessModalOpen(true)}>
              <Save className="h-5 w-5" />
              Save Changes
            </Button>
          </div>
        </main>
      </div>

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Success!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Settings saved successfully.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setSuccessModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestReviews;
